/**
 * Billing Stats hooks using React Relay
 *
 * Uses the billingStats query for server-side aggregation of
 * charges, refunds, wallet debits, and charge distribution.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useBillingStatsRelayQuery as UseBillingStatsRelayQuery } from './__generated__/useBillingStatsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const BillingStatsQuery = graphql`
  query useBillingStatsRelayQuery(
    $systemId: ID!
    $granularity: BillingGranularity!
    $startDate: DateTime
    $endDate: DateTime
  ) {
    billingStats(
      systemId: $systemId
      granularity: $granularity
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      averageChargeAmount
      averageRefundAmount
      totalChargeAmount
      totalChargeCount
      totalRefundAmount
      totalRefundCount
      chargesByPeriod {
        count
        period
        totalAmount
      }
      walletDebitsByPeriod {
        count
        period
        totalAmount
      }
      refundsByPeriod {
        count
        period
        totalAmount
      }
      chargeDistribution {
        count
        label
        maxCents
        minCents
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface BillingPeriodStatGraphQL {
  count: number;
  period: string;
  totalAmount: number;
}

export interface BillingBucketGraphQL {
  count: number;
  label: string;
  maxCents: number;
  minCents: number;
}

export interface BillingStatsGraphQL {
  id: string;
  averageChargeAmount: number | null;
  averageRefundAmount: number | null;
  totalChargeAmount: number | null;
  totalChargeCount: number | null;
  totalRefundAmount: number | null;
  totalRefundCount: number | null;
  chargesByPeriod: BillingPeriodStatGraphQL[] | null;
  walletDebitsByPeriod: BillingPeriodStatGraphQL[] | null;
  refundsByPeriod: BillingPeriodStatGraphQL[] | null;
  chargeDistribution: BillingBucketGraphQL[] | null;
}

export interface BillingStatsQueryParams {
  systemId: string;
  granularity: 'DAY' | 'MONTH' | 'YEAR';
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React Query-like hook for fetching billing stats.
 * Uses fetchQuery + useState for a simple request/response pattern.
 */
export function useBillingStatsRelayQuery(params: BillingStatsQueryParams) {
  const { systemId, granularity, startDate, endDate, enabled = true } = params;
  const [data, setData] = useState<BillingStatsGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!enabled || !systemId) return;

    setIsLoading(true);
    setError(null);

    const subscription = fetchQuery<UseBillingStatsRelayQuery>(
      relayEnvironment,
      BillingStatsQuery,
      {
        systemId,
        granularity,
        startDate,
        endDate,
      }
    ).subscribe({
      next: (response) => {
        setData(
          (response.billingStats as BillingStatsGraphQL | null) ?? null
        );
        setIsLoading(false);
        setIsFetched(true);
      },
      error: (err: Error) => {
        setError(err);
        setIsLoading(false);
        setIsFetched(true);
      },
    });

    return () => subscription.unsubscribe();
  }, [systemId, granularity, startDate, endDate, enabled]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  return { data, isLoading, isFetched, error, refetch: fetchData };
}
